import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search } from 'lucide-react';

import { useActiveSubject } from '../context/ActiveSubjectContext';
import { useAuth } from '../context/AuthContext';
import { knowledgeService } from '../services/knowledgeService';
import type { KnowledgeParticle } from '../types/domain';
import { getParticlePreviewText, getParticleTitle } from '../utils/particleDisplay';
import { SUBJECT_CONFIG } from '../utils/subjectConfig';

type ListScope = 'all' | 'no_spine';

type UnboundRow = {
  id: string;
  title: string | null;
  subject: string | null;
  question_preview: string | null;
  binding_status: string;
};

const PAGE_SIZE = 50;

function matchesKeyword(row: UnboundRow, keyword: string) {
  if (!keyword) return true;
  const needle = keyword.toLowerCase();
  return [row.title, row.question_preview, row.id]
    .map((value) => String(value || '').toLowerCase())
    .some((value) => value.includes(needle));
}

export function KnowledgePage() {
  const { user } = useAuth();
  const { activeSubject } = useActiveSubject();
  const subjectLabel = SUBJECT_CONFIG[activeSubject]?.label ?? activeSubject;
  const accent = SUBJECT_CONFIG[activeSubject]?.color ?? '#2563eb';

  const [listScope, setListScope] = useState<ListScope>('all');
  const [items, setItems] = useState<KnowledgeParticle[]>([]);
  const [unboundRows, setUnboundRows] = useState<UnboundRow[]>([]);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      if (listScope === 'no_spine') {
        const snapshot = await knowledgeService.listNoSpineBindingParticles({
          userId: user.id,
          limit: PAGE_SIZE,
          offset: 0,
          subject: subjectLabel,
        });
        const mapped = (snapshot.particles || []).map((row) => ({
          id: row.id,
          title: row.title,
          subject: row.subject,
          question_preview: row.question_preview,
          binding_status: row.binding_status,
        }));
        const filtered = mapped.filter((row) => matchesKeyword(row, appliedSearch.trim()));
        setUnboundRows(filtered);
        setItems([]);
        setTotalCount(snapshot.total);
      } else {
        const result = await knowledgeService.listKnowledgeParticles({
          userId: user.id,
          page: 1,
          pageSize: PAGE_SIZE,
          search: appliedSearch,
          subject: subjectLabel,
        });
        setItems(result.data);
        setUnboundRows([]);
        setTotalCount(result.pagination?.total ?? result.data.length);
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, listScope, subjectLabel, user?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const summaryLabel = useMemo(() => {
    if (loading) return '正在加载…';
    if (listScope === 'no_spine') return `未关联主干 ${totalCount} 条`;
    return `共 ${totalCount} 条知识颗粒`;
  }, [listScope, loading, totalCount]);

  const emptyLabel = listScope === 'no_spine'
    ? '当前学科没有未关联主干的知识颗粒。'
    : '暂无知识颗粒，请先在素材库上传并提取。';

  const filterOptions: Array<{ key: ListScope; label: string }> = [
    { key: 'all', label: '全部' },
    { key: 'no_spine', label: '未关联主干' },
  ];

  const visibleCount = listScope === 'no_spine' ? unboundRows.length : items.length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-ink">知识颗粒</h1>
        <p className="mt-1 text-sm text-muted">{summaryLabel}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {filterOptions.map((option) => {
          const active = option.key === listScope;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => setListScope(option.key)}
              className={
                active
                  ? 'rounded-full border px-3 py-1.5 text-sm font-semibold'
                  : 'rounded-full border border-border bg-surface px-3 py-1.5 text-sm font-medium text-muted hover:bg-black/[0.03]'
              }
              style={active ? { borderColor: accent, backgroundColor: `${accent}14`, color: accent } : undefined}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={16} />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') setAppliedSearch(search.trim());
          }}
          onBlur={() => setAppliedSearch(search.trim())}
          placeholder="搜索颗粒…"
          className="w-full rounded-lg border border-border py-2 pl-9 pr-3"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-primary" />
        </div>
      ) : null}

      {error ? <p className="text-danger">{error}</p> : null}

      {!loading && !error && visibleCount === 0 ? (
        <p className="text-muted">{emptyLabel}</p>
      ) : null}

      <ul className="divide-y divide-border rounded-xl border border-border bg-surface shadow-card">
        {listScope === 'no_spine'
          ? unboundRows.map((row) => {
            const title = String(row.question_preview || row.title || '').trim() || '未命名知识颗粒';
            return (
              <li key={row.id}>
                <Link to={`/knowledge/${row.id}`} className="block px-4 py-4 hover:bg-canvas">
                  <div className="font-medium text-ink line-clamp-2">{title}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-subtle">
                    <span>{row.subject || subjectLabel}</span>
                    <span className="rounded-md bg-amber-500/10 px-1.5 py-0.5 font-medium text-amber-700">
                      未关联主干
                    </span>
                  </div>
                </Link>
              </li>
            );
          })
          : items.map((item) => {
            const title = getParticleTitle(item);
            const preview = getParticlePreviewText(item);
            return (
              <li key={item.id}>
                <Link to={`/knowledge/${item.id}`} className="block px-4 py-4 hover:bg-canvas">
                  <div className="font-medium text-ink line-clamp-2">{title}</div>
                  <div className="mt-1 text-sm text-muted line-clamp-2">{preview}</div>
                  {item.subject ? <div className="mt-2 text-xs text-subtle">{item.subject}</div> : null}
                </Link>
              </li>
            );
          })}
      </ul>
    </div>
  );
}
